import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { CopyIcon, CodeIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Textarea } from 'src/components/ui/textarea';
import { useCopyToClipboard } from 'src/hooks/useCopyToClipboard';
import { parseViewDraft, viewDraftErrorKind } from './taskTypeViewDraft';
import { writeTaskPageDraft } from './taskTypeViewModel';

/**
 * The spec as JSON. Copy it to move a layout between types or environments;
 * paste one in and Apply to import it. The same panel is how an unreadable
 * saved layout is repaired: the text is shown with the first validation
 * error, and nothing is lost until Apply writes a spec that parses.
 */
export function TaskTypeViewJsonPanel({
  viewText,
  disabled,
  defaultOpen = false,
  onChange,
}: {
  viewText: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  onChange: (viewText: string) => void;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState<string | null>(null);
  const { copy, copiedValue } = useCopyToClipboard();
  const copied = copiedValue !== null;
  const shown = draft ?? viewText;
  const parsed = parseViewDraft(shown);
  const dirty = draft !== null && draft !== viewText;
  // Blank text would silently drop the layout; Discard and Use Standard
  // Review are the explicit, confirmed paths for that.
  const canApply = dirty && parsed.ok && shown.trim() !== '';

  function apply() {
    if (!canApply) return;
    onChange(writeTaskPageDraft(parsed.view));
    setDraft(null);
  }

  if (!open) {
    return (
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
        >
          <CodeIcon />
          <T context="task page builder, show the layout as JSON">
            Edit as JSON
          </T>
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-md border p-2"
      data-testid="task-type-view-json"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          <T context="task page builder JSON panel heading">Layout JSON</T>
        </span>
        <span className="text-xs text-muted-foreground">
          <T context="task page builder JSON panel hint">
            Copy to reuse this layout elsewhere, or paste one and apply.
          </T>
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={shown.trim() === ''}
            onClick={() => void copy(shown)}
          >
            <CopyIcon />
            {copied ? (
              <T context="task page builder, JSON copied confirmation">
                Copied
              </T>
            ) : (
              <T context="task page builder, copy the layout JSON">Copy</T>
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft(null);
              setOpen(false);
            }}
          >
            <T context="task page builder, hide the JSON panel">Hide</T>
          </Button>
        </div>
      </div>
      <Textarea
        value={shown}
        disabled={disabled}
        rows={14}
        spellCheck={false}
        className="font-mono text-xs"
        aria-label={gt('Layout JSON', {
          context: 'task page builder, textarea holding the layout spec',
        })}
        onChange={(event) => setDraft(event.target.value)}
      />
      {parsed.ok ? null : (
        <p className="text-xs text-destructive" role="alert">
          <JsonError kind={viewDraftErrorKind(parsed)} detail={parsed.detail} />
        </p>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={disabled || !canApply}
          onClick={apply}
        >
          <T context="task page builder, apply pasted layout JSON">Apply</T>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!dirty}
          onClick={() => setDraft(null)}
        >
          <T context="task page builder, discard JSON edits">Revert</T>
        </Button>
      </div>
    </div>
  );
}

function JsonError({
  kind,
  detail,
}: {
  kind: ReturnType<typeof viewDraftErrorKind>;
  detail: string;
}) {
  if (kind === 'invalid_json') {
    return (
      <T context="task page builder JSON panel, text is not JSON">
        This is not valid JSON.
      </T>
    );
  }
  if (kind === 'duplicate_ids') {
    return (
      <T context="task page builder JSON panel, repeated ids">
        Every section, placement, and column id must be unique.
      </T>
    );
  }
  return (
    <T context="task page builder JSON panel, spec validation error">
      Not a valid layout: <Var>{detail}</Var>
    </T>
  );
}
